import React, { useState, useEffect } from 'react';
import Head from 'next/head';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import useInitPurchase from '@hooks/useInitPurchase';
import FormLayout from '@/containers/formLayout';
import Address from '@/containers/address';
import PersonalData from '@/containers/personalData';
import ContactData from '@/containers/contactData';
import Payment from '@/containers/payment';
import SmartAppointment from '@/containers/smartAppointment';
import { SEGMENT, BUBBLE_TEXTS } from '@/constants/constant';
import { FORM_ID, TRANSACTION_TYPE } from '@/constants/pages/baf';
import { SMART_APPOINTMENT } from '@/constants/smartAppointment';
import getDeviceKindDependsOnViewPort from '@gcloud-npm/utils.common.get-device-kind-depends-on-viewport';
import { actions } from '@/reducers/form-baf-reducer';
import { actions as retargetingActions } from '@/reducers/retargeting-reducer';
import { INSTALLATION_ADDRESS } from '@/constants/installationAddress';
import FormBafFake from '@/components/fakes/formPagesFake/formBafFake';
import { sendTaggingInfo } from '@/utils/common';

const BafNoClientForm = ({ segment = SEGMENT.particulares }) => {
  const dispatch = useDispatch();
  const initialShoppingCart = useSelector(
    (state) => state.formBaf.initialShoppingCart
  );
  const shoppingCart = useSelector((state) => state.formBaf.shoppingCart);
  const appointmentData = useSelector((state) => state.appointment);
  const {
    firstAvailableAppointment,
    appointmentOptions,
    appointmentText,
    manualAppointmentText,
  } = { ...appointmentData };
  const loadingShoppingCart = useSelector(
    (state) => state.formBaf.loadingShoppingCart
  );
  const checkout = useSelector((state) => state.formBaf.checkout);
  const [showErrorBubble, setShowErrorBubble] = useState(false);

  const { unregister, getValues, handleSubmit, resetField, control, setValue } =
    useForm({
      mode: 'onTouched',
    });

  const horizontalAddress = useWatch({
    control,
    name: INSTALLATION_ADDRESS.HORIZONTAL_ADDRESS.ID,
  });
  const verticalAddress = useWatch({
    control,
    name: INSTALLATION_ADDRESS.VERTICAL_ADDRESS.ID,
  });
  const manualAddressChecked = useWatch({
    control,
    name: INSTALLATION_ADDRESS.MANUAL_ADDRESS_CHECKBOX.ID,
  });
  const automaticAddress =
    !manualAddressChecked && (horizontalAddress?.full || verticalAddress?.full);

  const onSubmit = async (formData) => {
    setShowErrorBubble(false);
    const deviceKind = getDeviceKindDependsOnViewPort();
    dispatch(actions.checkout({ formData, deviceKind }));
  };
  const onError = (formErrors) => {
    if (Object.keys(formErrors).length > 1) {
      setShowErrorBubble(true);
      if ('scrollTo' in window && typeof window.scrollTo === 'function') {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'instant',
        });
      }
    } else if (Object.keys(formErrors).length === 1) {
      setShowErrorBubble(false);
      const scrollElement = document.getElementById(Object.keys(formErrors)[0]);
      if (scrollElement) {
        scrollElement.scrollIntoView({ behavior: 'instant' });
      }
    }
  };

  const { setSegment, setFirstZone, initPurchase } = actions;
  useInitPurchase({
    segment,
    initPurchase,
    setSegment,
    setZone: setFirstZone,
    loaded: !!shoppingCart,
  });

  useEffect(() => {
    if (
      window.grecaptcha &&
      window.grecaptcha.enterprise
    ) {
      window.grecaptcha.enterprise.ready(() => {
        dispatch(
          retargetingActions.sendRetargeting({
            ...initialShoppingCart,
            campaignId: 'ret-baf-cart',
          })
        );
        const { telcoProductName, totalRecurringPrice } = {
          ...initialShoppingCart,
        };
        const { finalPrice } = {
          ...totalRecurringPrice,
        };
        sendTaggingInfo(
          {
            product: telcoProductName,
            price: finalPrice,
            funnelPurchaseMode: '',
            userType: '',
            funnelStepName: 'formulario',
            transactionType: TRANSACTION_TYPE,
            funnelFlowName: 'movistar-layer',
          },
          '',
          'movistar_layer_google_ads'
        );
      });
    }
  }, [dispatch]);

  return (
    <>
      <Head>
        <script
          async
          src={`https://www.google.com/recaptcha/enterprise.js?render=${process.env.NEXT_PUBLIC_LAYER_RECAPTCHA_ID}`}
        />
      </Head>
      <FormLayout
        pageId={FORM_ID.GP}
        onSubmit={handleSubmit(onSubmit, onError)}
        control={control}
        showErrorBubble={showErrorBubble}
        textBubble={BUBBLE_TEXTS.BUBBLE_ERROR_TEXT}
        loaded={!!shoppingCart}
        shoppingCart={shoppingCart}
        loadingShoppingCart={loadingShoppingCart}
        loadingButtonShoppingCart={checkout}
        urlLoginCardRedirect={shoppingCart ? document.location.href : ''}
        formFake={<FormBafFake />}
      >
        <PersonalData control={control} showTitle contactDataHidden />
        <Address
          loaded={!!shoppingCart}
          unregister={unregister}
          control={control}
          getValues={getValues}
          resetField={resetField}
          componentName={INSTALLATION_ADDRESS.ID}
          formStructure={INSTALLATION_ADDRESS}
          setInfo={actions.setInfo}
          setValue={setValue}
        />
        <ContactData control={control} showTitle />
        <SmartAppointment
          control={control}
          firstAvailableAppointment={
            automaticAddress && firstAvailableAppointment
              ? firstAvailableAppointment
              : null
          }
          appointmentOptions={
            automaticAddress && firstAvailableAppointment
              ? appointmentOptions
              : null
          }
          title={
            (!firstAvailableAppointment || manualAddressChecked) &&
            !loadingShoppingCart
              ? SMART_APPOINTMENT.TITLE_MANUAL_APPOINTMENT
              : null
          }
          loading={loadingShoppingCart}
          unregister={unregister}
          info={
            !manualAddressChecked &&
            automaticAddress &&
            !firstAvailableAppointment &&
            !loadingShoppingCart
              ? appointmentText
              : manualAddressChecked && manualAppointmentText
          }
        />
        <Payment loaded={!!shoppingCart} control={control} />
      </FormLayout>
    </>
  );
};
export default BafNoClientForm;
